import { useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Trash2, Info, Clock, Ban, Lock } from 'lucide-react';
import type { Task } from '../../types';
import { useAppStore } from '../../store';
import { useContextMenu } from '../../hooks/useContextMenu';
import { deleteTask, updateTask } from '../../hooks/useTasks';
import { queryClient } from '../../api/queryClient';
import { format } from 'date-fns';
import { useNearestFutureBlock } from '../../hooks/useTimelineBlocks';
import { timelineTargetHighlightClassName } from '../shared/highlightStyles';

interface SubtaskChipProps {
  task: Task;
  isCompleting?: boolean;
  /**
   * Compact rendering for narrow lanes — truncates title to a small fixed
   * width, hides the secondary scheduled-block badge, and shrinks the chip
   * footprint so chips don't overlap each other or the DONE trophy zone.
   * Full title is still available via the native title tooltip / detail panel.
   */
  isCompact?: boolean;
}

const priorityBorder = {
  p1: 'border-border-default',
  p2: 'border-border-default',
  p3: 'border-border-default',
};

const priorityDot = {
  p1: 'bg-p1',
  p2: 'bg-p2',
  p3: 'bg-p3',
};

export function SubtaskChip({ task, isCompleting, isCompact = false }: SubtaskChipProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
    data: {
      trackProgress: task.trackProgress,
      title: task.title,
      priority: task.priority,
      blocked: task.blocked,
      itemType: task.itemType,
      parentId: task.parentId,
      type: 'subtask',
    },
    disabled: !!task.blocked,
  });
  const didDrag = useRef(false);
  const ctxMenu = useContextMenu();

  const highlightedTaskId = useAppStore((s) => s.highlightedTaskId);
  const isHighlighted = highlightedTaskId === task.id;

  const clampedProgress = Math.max(0, Math.min(100, task.trackProgress));
  const nearestBlock = useNearestFutureBlock(task.id);

  // Chip footprint along the progress axis. The clamp formula keeps the
  // chip's center on the progress %, but bounds it to the lane edges so
  // chips at 0 % or 100 % don't poke past the DONE trophy zone.
  // Compact chips are fixed-width so they can never overflow the lane.
  const chipWidthPx = isCompact ? 88 : 140;
  const chipHalfPx = chipWidthPx / 2;

  const style: React.CSSProperties = {
    position: 'absolute',
    left: `clamp(0px, calc(${clampedProgress}% - ${chipHalfPx}px), calc(100% - ${chipWidthPx}px))`,
    top: '50%',
    transform: `translateY(-50%)${transform ? ` translate(${transform.x}px, ${transform.y}px)` : ''}`,
    zIndex: isDragging ? 50 : 1,
    cursor: task.blocked ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
  };

  if (isDragging) {
    didDrag.current = true;
  } else if (didDrag.current) {
    setTimeout(() => { didDrag.current = false; }, 50);
  }

  const handleClick = () => {
    if (didDrag.current) return;
    useAppStore.getState().setSelectedTaskId(task.id);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ctxMenu.open(e.clientX, e.clientY, [
      {
        label: 'View details',
        icon: <Info size={13} />,
        onClick: () => useAppStore.getState().setSelectedTaskId(task.id),
      },
      {
        label: task.blocked ? 'Unblock' : 'Mark as blocked',
        icon: <Ban size={13} />,
        onClick: async () => {
          await updateTask(task.id, { blocked: !task.blocked });
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        },
      },
      {
        label: 'Delete subtask',
        icon: <Trash2 size={13} />,
        danger: true,
        onClick: async () => { await deleteTask(task.id, { parentId: task.parentId }); },
      },
    ]);
  };

  const dotClass = `rounded-full shrink-0 ${
    isDragging && isCompleting
      ? 'bg-finish shadow-[0_0_8px_var(--color-finish)]'
      : priorityDot[task.priority]
  } ${task.priority === 'p1' && !(isDragging && isCompleting) ? 'shadow-[0_0_6px_var(--color-p1)]' : ''}`;

  const progressNumberClass = `tabular-nums shrink-0 ${
    clampedProgress === 100
      ? 'text-finish'
      : clampedProgress >= 75
        ? 'text-text-primary'
        : 'text-text-secondary'
  }`;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      title={isCompact ? task.title : undefined}
      className={`select-none rounded-xl overflow-hidden border bg-bg-raised
        shadow-lg shadow-black/25 hover:border-accent/60 hover:shadow-xl hover:shadow-black/30
        ${priorityBorder[task.priority]}
        ${isDragging && !isCompleting ? 'border-accent drag-glow scale-[1.05]' : ''}
        ${isDragging && isCompleting ? 'border-finish ring-2 ring-finish/70 shadow-xl shadow-finish/25 animate-pulse scale-[1.05]' : ''}
        ${task.blocked ? 'opacity-50' : ''}
        ${isHighlighted ? timelineTargetHighlightClassName : 'transition-[border-color,box-shadow] duration-150'}`}
      style={style}
    >
      {isCompact ? (
        <div className="flex items-center gap-1.5 px-2 py-1 w-[88px]">
          <div className={`w-2 h-2 ${dotClass}`} />
          {!!task.blocked && <Lock size={9} className="text-text-muted shrink-0" />}
          <span className="text-micro font-medium text-text-primary flex-1 truncate min-w-0">
            {task.title}
          </span>
          <span className={`text-micro font-bold ${progressNumberClass}`}>
            {clampedProgress}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-3 py-1.5 min-w-32 max-w-72">
          <div className={`w-2.5 h-2.5 ${dotClass}`} />
          {!!task.blocked && <Lock size={10} className="text-text-muted shrink-0" />}
          <span className="text-caption font-medium text-text-primary flex-1 truncate">
            {task.title}
          </span>
          {nearestBlock && (
            <span className="flex items-center gap-0.5 shrink-0">
              <Clock size={10} className="text-accent/70" />
              <span className="text-micro font-mono text-accent/70">
                {nearestBlock.date !== format(new Date(), 'yyyy-MM-dd')
                  ? `${format(new Date(nearestBlock.date + 'T00:00:00'), 'MMM d')} `
                  : ''}{nearestBlock.scheduledStartTime}
              </span>
            </span>
          )}
          <span className={`text-micro font-bold ${progressNumberClass}`}>
            {clampedProgress}%
          </span>
        </div>
      )}
      <div className="h-1 bg-accent/15">
        <div
          className={`h-full transition-[width] duration-300 ease-out ${clampedProgress === 100 ? 'bg-finish' : 'bg-accent'} ${clampedProgress >= 90 ? 'shadow-[0_0_8px_var(--color-finish)]' : ''}`}
          style={{ width: `${Math.max(clampedProgress, 2)}%` }}
        />
      </div>
    </div>
  );
}
